/**
 * Job Application Status Enum
 * Belongs to JobApplicant entity
 *
 * NOTE:
 * OnboardingDone and Hired are intentionally kept as separate statuses.
 *
 * Reason:
 * - Onboarding and employee/job creation is a multi-step process
 * - HR may complete onboarding activities but delay or forget final hiring/job creation
 * - The system must preserve intermediate progress so HR can continue later
 * - Hired means the recruitment process is fully finalized
 *
 * IMPORTANT:
 * Detailed interview workflow states are intentionally NOT stored here.
 * Interview scheduling, slot selection, round progress, and completion
 * are managed separately through Interview Round entities.
 *
 * This enum only represents the high-level hiring pipeline state.
 */

export const JobApplicationStatusEnum = Object.freeze({
  Applied: 0, // Applicant submitted application for the job posting

  ShortList: 1, // HR shortlisted the applicant for further evaluation

  TermsAccepted: 2, // Applicant received and accepted preliminary terms/credentials


  InterviewInProgress: 3, // One or more interview rounds are currently in progress
  // (scheduling, slot selection, or interview execution)

  InterviewCompleted: 4, // All required interview rounds have been completed


  FeedbackAdded: 5, // HR submitted final overall interview/application feedback

  DocumentSend: 6, // HR sent hiring/contract documents to applicant

  DocumentRecieved: 7, // Applicant returned signed/required documents

  OnboardingDone: 8, // HR completed onboarding activities

  Hired: 9, // Applicant hiring process fully completed and finalized

  Reject: 10, // Applicant rejected at any stage of the hiring pipeline
});


// =======================
// Labels for UI Display
// =======================

export const JobApplicationStatusLabel = Object.freeze({
  [JobApplicationStatusEnum.Applied]: "Applied",

  [JobApplicationStatusEnum.ShortList]: "Shortlisted",

  [JobApplicationStatusEnum.TermsAccepted]: "Terms Accepted",

  [JobApplicationStatusEnum.InterviewInProgress]:
    "Interview In Progress",

  [JobApplicationStatusEnum.InterviewCompleted]:
    "Interview Completed",

  [JobApplicationStatusEnum.FeedbackAdded]:
    "Feedback Added",

  [JobApplicationStatusEnum.DocumentSend]:
    "Documents Sent",

  [JobApplicationStatusEnum.DocumentRecieved]:
    "Documents Received",

  [JobApplicationStatusEnum.OnboardingDone]:
    "Onboarding Completed",

  [JobApplicationStatusEnum.Hired]:
    "Hired",

  [JobApplicationStatusEnum.Reject]:
    "Rejected",
});

// =====================================
// Muted / Helper Text for Status Detail
// =====================================

export const JobApplicationStatusText = Object.freeze({
  [JobApplicationStatusEnum.Applied]:
    "The applicant has submitted an application for the position.",

  [JobApplicationStatusEnum.ShortList]:
    "HR reviewed and shortlisted the applicant for further evaluation.",

  [JobApplicationStatusEnum.TermsAccepted]:
    "The applicant received and accepted the preliminary hiring terms and credentials.",

  [JobApplicationStatusEnum.InterviewInProgress]:
    "One or more interview rounds are currently in progress.",

  [JobApplicationStatusEnum.InterviewCompleted]:
    "All required interview rounds have been completed.",

  [JobApplicationStatusEnum.FeedbackAdded]:
    "HR submitted final interview/application feedback.",

  [JobApplicationStatusEnum.DocumentSend]:
    "HR sent hiring or contract documents to the applicant.",

  [JobApplicationStatusEnum.DocumentRecieved]:
    "The applicant returned signed or required documents.",

  [JobApplicationStatusEnum.OnboardingDone]:
    "HR completed onboarding activities. Final hiring may still be pending.",

  [JobApplicationStatusEnum.Hired]:
    "The applicant has been officially hired. Recruitment process is fully finalized.",

  [JobApplicationStatusEnum.Reject]:
    "The applicant was rejected during the hiring process.",
});

// =======================
// Status Config
// =======================

export const JOB_STATUS_CONFIG = {
  [JobApplicationStatusEnum.Applied]: {
    key: "applied",
    label: JobApplicationStatusLabel[0],
  },

  [JobApplicationStatusEnum.ShortList]: {
    key: "shortList",
    label: JobApplicationStatusLabel[1],
  },

  [JobApplicationStatusEnum.TermsAccepted]: {
    key: "termsAccepted",
    label: JobApplicationStatusLabel[2],
  },

  [JobApplicationStatusEnum.InterviewInProgress]: {
    key: "interviewInProgress",
    label: JobApplicationStatusLabel[3],
  },

  [JobApplicationStatusEnum.InterviewCompleted]: {
    key: "interviewCompleted",
    label: JobApplicationStatusLabel[4],
  },

  [JobApplicationStatusEnum.FeedbackAdded]: {
    key: "feedbackAdded",
    label: JobApplicationStatusLabel[5],
  },

  [JobApplicationStatusEnum.DocumentSend]: {
    key: "documentSend",
    label: JobApplicationStatusLabel[6],
  },

  [JobApplicationStatusEnum.DocumentRecieved]: {
    key: "documentRecieved",
    label: JobApplicationStatusLabel[7],
  },

  [JobApplicationStatusEnum.OnboardingDone]: {
    key: "onboardingDone",
    label: JobApplicationStatusLabel[8],
  },

  [JobApplicationStatusEnum.Hired]: {
    key: "hired",
    label: JobApplicationStatusLabel[9],
  },

  [JobApplicationStatusEnum.Reject]: {
    key: "reject",
    label: JobApplicationStatusLabel[10],
  },
};